import React, { useState } from "react";
import MaterialListSelect from "./MaterialListSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import axios from "axios";
import { useSelector } from "react-redux";
import { TbTrashX } from "react-icons/tb";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import { formatNumber } from "../utils/numbers";
import { v4 as uuidv4 } from "uuid";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1";

function StockInOut({ stockType }) {
  const isIn = stockType === "Stock In";
  const C = isIn
    ? {
        header: "bg-green-700",
        btn: "bg-green-700 hover:bg-green-800",
        ring: "focus:ring-green-400",
        hover: "hover:bg-green-50",
        qty: "text-green-700",
      }
    : {
        header: "bg-red-700",
        btn: "bg-red-700 hover:bg-red-800",
        ring: "focus:ring-red-400",
        hover: "hover:bg-red-50",
        qty: "text-red-700",
      };

  const [selectedMatList, setSelectedMatList] = useState(null);
  const [selectedMatListName, setSelectedMatListName] = useState("");
  const [stockQty, setStockQty] = useState("");
  const [remark, setRemark] = useState("");
  const [transFor, setTransFor] = useState("");
  const { user } = useSelector((state) => state.auth);
  const [tempArray, setTempArray] = useState([]);
  const [productLocation, setProductLocation] = useState("0");
  const [currStock, setCurrStock] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);

  const handleMatListChange = (Arr) => {
    setSelectedMatList(Arr[0]);
    setSelectedMatListName(Arr[1]);
    setStockQty("");
    getCurrentStock(Arr[0]);
  };

  const handleQtyChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^[0-9]+$/.test(v)) setStockQty(v);
  };

  const addObject = () => {
    if (!stockQty || !selectedMatList || !selectedMatListName) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    if (!isIn && Number(stockQty) > (currStock?.current_stock || 0)) {
      toast.warning("Stock Out qty exceeds available stock.");
      return;
    }
    const newObj = {
      no: tempArray.length + 1,
      product_id: selectedMatList,
      product_name: selectedMatListName,
      temp_qty: stockQty,
      stock_in: isIn ? stockQty : 0,
      stock_out: isIn ? 0 : stockQty,
      transaction_type: stockType,
      transaction_for: transFor,
      transaction_by: user.username,
      transaction_remark: remark,
    };
    setTempArray([...tempArray, newObj]);
    setStockQty("");
  };

  const deleteObject = (no) =>
    setTempArray(tempArray.filter((i) => i.no !== no));

  const insertStock = async () => {
    setIsLoading(true);
    try {
      const groupId = isIn ? uuidv4() : null;
      const transactions = tempArray.map((t) => ({
        ...t,
        transaction_type: isIn ? "Pending Stock In" : t.transaction_type,
        transaction_group_id: isIn ? groupId : (t.transaction_group_id ?? null),
      }));
      const res = await axios.post(
        "https://eps.elkomprintama.com/post/api/store/stocks",
        { transactions },
      );
      if (res.status === 200) toast.success(res.data.msg);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Insert failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStock = async (product_id) => {
    if (!product_id) {
      setCurrStock(null);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstock/${product_id}`,
      );
      if (res.status === 200) setCurrStock(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tempArray.length === 0) {
      toast.warning("Please add at least one item.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className={`w-full rounded-lg ${C.header} mb-4 flex-shrink-0`}>
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          {stockType}
        </label>
      </div>

      {/* Body card */}
      <form
        onSubmit={handleSubmit}
        className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      >
        {/* Form section */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4">
          {/* Location + Material + Balance Stock — one row */}
          <div className="flex gap-3 items-stretch">
            <div className="w-44 flex-shrink-0">
              <MaterialLocationSelect
                selectedLocation={productLocation}
                onChangeList={setProductLocation}
              />
            </div>
            <div className="flex-1 min-w-0">
              <MaterialListSelect
                onChangeList={handleMatListChange}
                location_id={productLocation}
              />
            </div>
            <div className="flex-shrink-0 flex flex-col items-center justify-center px-5 py-3 rounded-lg border bg-gray-50 border-gray-200 min-w-[130px]">
              <span className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-1">
                Balance Stock
              </span>
              {currStock !== null ? (
                <span
                  className={`font-bold text-2xl ${currStock.current_stock === 0 ? "text-red-600" : "text-green-600"}`}
                >
                  {formatNumber(currStock.current_stock)} {currStock.unit}
                </span>
              ) : (
                <span className="text-gray-300 text-2xl font-bold">—</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>QTY {stockType}</label>
              <input
                className={`w-full border border-gray-300 rounded-lg px-3 py-2.5 text-lg font-bold focus:outline-none focus:ring-1 ${C.ring}`}
                type="text"
                placeholder="e.g. 10"
                value={stockQty}
                onChange={handleQtyChange}
              />
            </div>
            <div>
              <label className={lbl}>
                {isIn ? "Remark" : "Remark / Operator"}
              </label>
              <input
                className={`${inp} ${C.ring}`}
                type="text"
                placeholder={isIn ? "Remark" : "Remark / Operator"}
                value={remark}
                onChange={(e) => setRemark(e.target.value.toUpperCase())}
              />
            </div>
            <div>
              <label className={lbl}>{isIn ? "Supplier" : "Customer"}</label>
              <input
                className={`${inp} ${C.ring}`}
                type="text"
                placeholder={isIn ? "Supplier name" : "Customer name"}
                value={transFor}
                onChange={(e) => setTransFor(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={addObject}
            className={`w-full py-2.5 rounded-xl text-sm font-bold text-white transition ${C.btn}`}
          >
            Add to List
          </button>
        </div>

        {/* Staging table — fixed header + scrollable body */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 pt-4">
          {/* Header — never scrolls */}
          <table className="w-full text-xs table-fixed flex-shrink-0">
            <colgroup>
              <col style={{ width: "44px" }} />
              <col />
              <col style={{ width: "60px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "44px" }} />
            </colgroup>
            <thead className="bg-gray-700 text-white uppercase">
              <tr>
                <th className="px-3 py-3 text-left border-r">No</th>
                <th className="px-3 py-3 text-left border-r">Product Name</th>
                <th className="px-3 py-3 text-center border-r">QTY</th>
                <th className="px-3 py-3 text-left border-r">Type</th>
                <th className="px-3 py-3 text-left border-r">Remark</th>
                <th className="px-3 py-3 text-left border-r">
                  {isIn ? "Supplier" : "Customer"}
                </th>
                <th className="px-3 py-3 text-left border-r">Input By</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
          </table>
          {/* Body — scrolls independently */}
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <table className="w-full text-xs border-collapse table-fixed">
              <colgroup>
                <col style={{ width: "44px" }} />
                <col />
                <col style={{ width: "60px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "44px" }} />
              </colgroup>
              <tbody>
                {tempArray.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-12 text-center text-gray-400 italic text-sm"
                    >
                      No items added yet. Fill in the form above and click Add
                      to List.
                    </td>
                  </tr>
                ) : (
                  tempArray.map((data, i) => (
                    <tr
                      key={data.no}
                      className={`border-b ${C.hover} ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    >
                      <td className="px-3 py-3 border-r text-gray-500">
                        {data.no}
                      </td>
                      <td
                        className="px-3 py-3 border-r font-medium text-gray-700 truncate"
                        title={data.product_name}
                      >
                        {data.product_name}
                      </td>
                      <td
                        className={`px-3 py-3 border-r text-center font-bold ${C.qty}`}
                      >
                        {formatNumber(data.temp_qty)}
                      </td>
                      <td className="px-3 py-3 border-r">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${isIn ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                        >
                          {data.transaction_type}
                        </span>
                      </td>
                      <td
                        className="px-3 py-3 border-r text-gray-600 truncate"
                        title={data.transaction_remark}
                      >
                        {data.transaction_remark || "—"}
                      </td>
                      <td
                        className="px-3 py-3 border-r text-gray-600 truncate"
                        title={data.transaction_for}
                      >
                        {data.transaction_for || "—"}
                      </td>
                      <td className="px-3 py-3 border-r text-gray-500 truncate">
                        {data.transaction_by}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => deleteObject(data.no)}
                          className="text-red-400 hover:text-red-600 transition"
                        >
                          <TbTrashX size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Submit footer — always pinned at bottom */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="submit"
            disabled={tempArray.length === 0}
            className={`w-full py-3 rounded-xl text-sm font-bold text-white transition ${C.btn} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {tempArray.length > 0
              ? `Submit ${stockType} (${tempArray.length} item${tempArray.length !== 1 ? "s" : ""})`
              : `Submit ${stockType}`}
          </button>
        </div>
      </form>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message={`Submit this ${stockType} transaction?`}
          onConfirm={async () => {
            setShowConfirm(false);
            await insertStock();
            setRemark("");
            setStockQty("");
            setTempArray([]);
            if (selectedMatList) getCurrentStock(selectedMatList);
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default StockInOut;
